import { CustomToast } from "@/components/ui/molecules/CustomToast";
import { createElement } from "react";
import { toast as sonnerToast } from "sonner";

type ToastInput = string | {
    title?: string;
    description?: string;
    duration?: number;
    copyText?: string;
    action?: { label: string; onClick: () => void };
    /** The action this toast answers (a form, a row's op). A later toast with the same key replaces it, so a form's
     *  success clears the failure it showed before (ruled 2026-11-01). */
    key?: string;
    /** The action ends in a full page load on this origin (`window.location`), which would clear the toast before it
     *  is seen: it is kept in this tab's sessionStorage and shown once the next page's Toaster mounts. */
    onNextPage?: boolean;
};

const NEXT_PAGE_STORAGE_KEY = "dw-toast-next-page";
/** A stored toast older than this is from an action whose navigation never happened; it is dropped, not shown. */
const NEXT_PAGE_MAX_AGE_MS = 15_000;
type ToastType = "success" | "error" | "warning" | "info";

/** Counts showings, so a keyed toast replaced in place restarts its countdown (CustomToast `showing`). */
let showings = 0;

const recentToasts = new Map<string, number>();
const DEDUP_WINDOW_MS = 1000;

function shouldShow(key: string): boolean {
    const now = Date.now();
    const last = recentToasts.get(key);
    if (last && now - last < DEDUP_WINDOW_MS) {
        return false;
    }
    recentToasts.set(key, now);
    for (const [k, t] of recentToasts) {
        if (now - t > DEDUP_WINDOW_MS) {
            recentToasts.delete(k);
        }
    }
    return true;
}

function normalize(input: ToastInput): { message: string; options: {
    description?: string;
    duration?: number;
    copyText?: string;
    action?: { label: string; onClick: () => void };
    key?: string;
}; } {
    if (typeof input === "string") {
        return { message: input, options: {} };
    }
    return { message: input.title ?? "", options: { description: input.description, duration: input.duration, copyText: input.copyText, action: input.action, key: input.key } };
}

function showToast(type: ToastType, input: ToastInput) {
    if (typeof input !== "string" && input.onNextPage) {
        try {
            sessionStorage.setItem(NEXT_PAGE_STORAGE_KEY, JSON.stringify({ type, input: { ...input, onNextPage: false }, at: Date.now() }));
            return;
        } catch {
            // No storage (a private window): show it now, as before.
        }
    }
    const { message, options } = normalize(input);
    const dedupKey = `${type}:${message}`;
    if (!shouldShow(dedupKey)) {
        return;
    }

    const duration = type === "error" ? Infinity : (options.duration ?? 4000);

    const showing = ++showings;
    sonnerToast.custom((id) => createElement(CustomToast, { id, type, message, description: options.description, duration, copyText: options.copyText, action: options.action, showing }), { duration: Infinity, ...(options.key ? { id: `dw-toast:${options.key}` } : {}) });
}

/** The toast an action left for this page (onNextPage), shown once: the Toaster calls this when it mounts. */
export function showNextPageToast() {
    let stored: string | null = null;
    try {
        stored = sessionStorage.getItem(NEXT_PAGE_STORAGE_KEY);
        sessionStorage.removeItem(NEXT_PAGE_STORAGE_KEY);
    } catch {
        return;
    }
    if (!stored) {
        return;
    }
    try {
        const { type, input, at } = JSON.parse(stored) as { type: ToastType; input: ToastInput; at: number };
        if (Date.now() - at <= NEXT_PAGE_MAX_AGE_MS) {
            showToast(type, input);
        }
    } catch {
        // A malformed entry is dropped.
    }
}

export const sileo = { success: (input: ToastInput) => showToast("success", input), error: (input: ToastInput) => showToast("error", input), warning: (input: ToastInput) => showToast("warning", input), info: (input: ToastInput) => showToast("info", input), show: (input: ToastInput) => showToast("info", input), action: (input: ToastInput) => showToast("info", input), dismiss: sonnerToast.dismiss, clear: () => sonnerToast.dismiss() };

